import type { ReactText } from 'react';
import React, { useRef } from 'react';
import type { ModalProps } from '../ui/Modal/Modal';
import { Modal } from '../ui';
import type { CoaFormSaveType } from './CoaForm';
import CoaForm, { parseCommissionNodes } from './CoaForm';
import { useEditCoaModalQuery } from '../../graphql';
import type { NotNullDataPrice } from './coa';
import { useUpdateCoaWithRelationsMutation } from './coa';
import { isDefined } from '../../lib/util';
import { toast } from 'react-toastify';
import { useCurrentUser } from '../../state/zustand';
import { handleCoaError } from './coaErrorHandling';

export interface EditCoaModalProps extends Omit<ModalProps, 'isOpen'> {
  coaId?: number;
  onEdit?: () => void;
}

const EditCoaModal = ({
  coaId,
  onClose,
  onEdit,
  ...restProps
}: EditCoaModalProps): JSX.Element | null => {
  const { data, refetch } = useEditCoaModalQuery({
    variables: { coaId: coaId ?? NaN },
    skip: typeof coaId !== 'number',
  });

  const coa = data?.coaContractById ?? undefined;
  const [updateCoaWithRelations] = useUpdateCoaWithRelationsMutation();
  const currentUser = useCurrentUser();
  const saveToastId = useRef<ReactText | null>(null);
  const oldCommissions = parseCommissionNodes(
    coa?.commissions.nodes.filter(isDefined) ?? [],
  );

  const uniqueOldCommissions = Object.values(
    oldCommissions.reduce<Record<string, (typeof oldCommissions)[number]>>(
      (acc, cur) => {
        const key = `${cur.type}-${cur.value}-${cur.organizationId}`;
        acc[key] = acc[key] || cur;
        return acc;
      },
      {},
    ),
  );
  const handleSave = async (data: CoaFormSaveType): Promise<unknown> => {
    const userId = currentUser?.id ?? null;
    if (!userId || !coa?.id) return;

    saveToastId.current = toast('Saving COA', {
      type: toast.TYPE.INFO,
      isLoading: true,
    });

    const notNullDataPrices = data.prices.filter(({ price }) =>
      isDefined(price),
    ) as NotNullDataPrice[];

    try {
      await updateCoaWithRelations(
        {
          id: coa.id,
          ...data.baseContract,
          externalId: data.baseContract.externalId?.length
            ? data.baseContract.externalId
            : null,
        },

        data.bunkers,
        coa.bunkers.nodes.filter(isDefined),
        data.portPairs.map((portPair) => ({
          chartererId: userId,
          ...portPair,
        })),
        coa.portPairs.nodes.filter(isDefined),
        data.portData,
        coa.portData.nodes
          .map((pd) => pd?.portDatumByPortDataId)
          .filter(isDefined),
        data.assortments,
        coa.coaAssortments.nodes.filter(isDefined),
        notNullDataPrices,
        coa.prices.nodes.filter(isDefined),
        data.vessels,
        coa.vessels.nodes.filter(isDefined),
        data.commissions,
        uniqueOldCommissions,
        data.portCosts,
        coa.portCosts.nodes.filter(isDefined),
      );

      toast.update(saveToastId.current, {
        render: 'COA saved successfully',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
      });

      await refetch();
      onEdit?.();
    } catch (error) {
      handleCoaError(error, saveToastId.current, data.baseContract.externalId);
    }
  };

  return (
    <Modal isOpen onClose={onClose} {...restProps}>
      {coa && <CoaForm coa={coa} onSave={handleSave} onClose={onClose} />}
    </Modal>
  );
};

export default EditCoaModal;
